import React, { useState, useEffect } from 'react';
import {
	Container,
	Grid,
	Card,
	CardContent,
	CardActions,
	Button,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	CircularProgress,
	Box,
	IconButton,
	Snackbar
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import MuiAlert from '@mui/material/Alert';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { useUserState } from '../../hook/customerHook';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3)
	},
	card: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		cursor: 'pointer',
		transition: 'transform 0.2s',
		'&:hover': {
			transform: 'scale(1.02)'
		}
	},
	cardContent: {
		flexGrow: 1
	},
	addCard: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: 'pointer',
		border: '2px dashed #ccc',
		transition: 'transform 0.2s',
		'&:hover': {
			transform: 'scale(1.02)',
			borderColor: theme.palette.primary.main
		}
	},
	dialogContent: {
		minWidth: '400px'
	},
	deleteButton: {
		position: 'absolute',
		top: theme.spacing(1),
		right: theme.spacing(1),
		'&:hover': {
			backgroundColor: 'rgba(255, 0, 0, 0.1)'
		}
	},
	cardContainer: {
		position: 'relative'
	}
}));

const TemplateSelector = ({ onTemplateSelect, onNewTemplate }) => {
	const classes = useStyles();
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [openDialog, setOpenDialog] = useState(false);
	const [newTemplateName, setNewTemplateName] = useState('');
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [templateToDelete, setTemplateToDelete] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
	const [letterheadFile, setLetterheadFile] = useState(null);
	const [uploadError, setUploadError] = useState(null);
	const [userState] = useUserState();
	const [selectingTemplate, setSelectingTemplate] = useState(false);

	useEffect(() => {
		fetchTemplates();
	}, []);

	const fetchTemplates = async () => {
		setLoading(true);
		try {
			const response = await axios.get('/api/letter_templates');
			const templateData = Array.isArray(response.data) ? response.data : [];
			setTemplates(templateData);
		} catch (error) {
			console.error('Error fetching templates:', error);
			setTemplates([]);
		} finally {
			setLoading(false);
		}
	};

	const filteredTemplates = templates
		.filter((t) => Number(t.organization_id) === Number(userState?.user?.organizationId))
		.sort((a, b) => {
			const aTime = new Date(a.created_at || a.last_updated || 0).getTime();
			const bTime = new Date(b.created_at || b.last_updated || 0).getTime();
			return bTime - aTime;
		});

	const handleCreateNew = () => {
		setOpenDialog(true);
	};

	const handleDialogClose = () => {
		setOpenDialog(false);
		setNewTemplateName('');
		setLetterheadFile(null);
		setUploadError(null);
	};

	const handleCreateTemplate = async () => {
		if (newTemplateName.trim()) {
			try {
				let letterheadFileName = null;

				if (letterheadFile) {
					if (!letterheadFile.name.toLowerCase().endsWith('.pdf')) {
						setUploadError('Only PDF files are allowed');
						return;
					}

					const formData = new FormData();
					formData.append('file', letterheadFile);

					const response = await axios.post('/api/letterheads/upload', formData, {
						headers: {
							'Content-Type': 'multipart/form-data'
						}
					});

					if (response.data.success) {
						letterheadFileName = response.data.fileName;
					} else {
						setUploadError('Failed to upload letterhead');
						return;
					}
				}

				onNewTemplate(newTemplateName.trim(), letterheadFileName);
				handleDialogClose();
			} catch (error) {
				console.error('Error creating template:', error);
				setUploadError('Failed to create template. Please try again.');
			}
		}
	};

	const handleDeleteClick = (template, event) => {
		event.stopPropagation();
		setTemplateToDelete(template);
		setDeleteConfirmOpen(true);
	};

	const handleDeleteConfirm = async () => {
		try {
			await axios.delete(`/api/letter_templates/${templateToDelete.id}`);
			setSnackbar({
				open: true,
				message: 'Template deleted successfully',
				severity: 'success'
			});
			fetchTemplates();
		} catch (error) {
			console.error('Error deleting template:', error);
			setSnackbar({
				open: true,
				message: 'Failed to delete template',
				severity: 'error'
			});
		}
		setDeleteConfirmOpen(false);
		setTemplateToDelete(null);
	};

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false);
		setTemplateToDelete(null);
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setSnackbar({ ...snackbar, open: false });
	};

	const handleTemplateSelect = async (template) => {
		setSelectingTemplate(true);
		try {
			await onTemplateSelect(template);
		} finally {
			setSelectingTemplate(false);
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<div className={classes.root}>
			<Grid container spacing={3}>
				<Grid item xs={12} sm={6} md={4}>
					<Card className={classes.addCard} onClick={handleCreateNew}>
						<AddIcon style={{ fontSize: 48, color: '#666' }} />
						<Typography variant="h6" color="textSecondary">
							Create New Template
						</Typography>
					</Card>
				</Grid>
				{filteredTemplates.map((template) => (
					<Grid item xs={12} sm={6} md={4} key={template.id}>
						<div className={classes.cardContainer}>
							<Card
								className={classes.card}
								onClick={() => handleTemplateSelect(template)}
							>
								<CardContent className={classes.cardContent}>
									<Typography gutterBottom variant="h5" component="h2">
										{template.name || 'Unnamed Template'}
									</Typography>
									<Typography color="textSecondary">
										Last updated:{' '}
										{new Date(template.last_updated).toLocaleDateString()}
									</Typography>
								</CardContent>
								<CardActions>
									<Button size="small" color="primary">
										Select Template
									</Button>
								</CardActions>
							</Card>
							<IconButton
								className={classes.deleteButton}
								onClick={(e) => handleDeleteClick(template, e)}
								size="small"
							>
								<DeleteIcon />
							</IconButton>
						</div>
					</Grid>
				))}
			</Grid>

			{(loading || selectingTemplate) && (
				<Box
					position="fixed"
					top={0}
					left={0}
					width="100vw"
					height="100vh"
					zIndex={2000}
					display="flex"
					alignItems="center"
					justifyContent="center"
					style={{ background: 'rgba(255,255,255,0.6)' }}
				>
					<CircularProgress />
				</Box>
			)}

			<Dialog open={openDialog} onClose={handleDialogClose}>
				<DialogTitle>Create New Template</DialogTitle>
				<DialogContent className={classes.dialogContent}>
					<TextField
						autoFocus
						margin="dense"
						label="Template Name"
						type="text"
						fullWidth
						value={newTemplateName}
						onChange={(e) => setNewTemplateName(e.target.value)}
					/>
					<Box mt={2}>
						<input
							accept=".pdf"
							style={{ display: 'none' }}
							id="letterhead-file"
							type="file"
							onChange={(e) => setLetterheadFile(e.target.files[0])}
						/>
						<label htmlFor="letterhead-file">
							<Button
								variant="outlined"
								component="span"
								fullWidth
								startIcon={<AddPhotoAlternateIcon />}
							>
								Upload Letterhead (Optional)
							</Button>
						</label>
						{letterheadFile && (
							<Typography variant="body2" style={{ marginTop: '8px' }}>
								Selected file: {letterheadFile.name}
							</Typography>
						)}
						{uploadError && (
							<Typography color="error" variant="body2" style={{ marginTop: '8px' }}>
								{uploadError}
							</Typography>
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDialogClose} color="primary">
						Cancel
					</Button>
					<Button onClick={handleCreateTemplate} color="primary">
						Create
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
				<DialogTitle>Delete Template</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete the template "{templateToDelete?.name}"?
						This action cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleDeleteCancel} color="primary">
						Cancel
					</Button>
					<Button onClick={handleDeleteConfirm} color="secondary">
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<MuiAlert
					elevation={6}
					variant="filled"
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
				>
					{snackbar.message}
				</MuiAlert>
			</Snackbar>
		</div>
	);
};

export default TemplateSelector;
